import { useResizeObserver } from '@vueuse/core';
import { computed, shallowRef, type Ref } from 'vue';

/** Совпадает с шириной затухания `2rem`: выбранный элемент докручивается за него. */
const FADE_PX = 32;

const MASKS = {
  x: ['mask-l-from-[calc(100%-2rem)]', 'mask-r-from-[calc(100%-2rem)]'],
  y: ['mask-t-from-[calc(100%-2rem)]', 'mask-b-from-[calc(100%-2rem)]'],
} as const;

export interface UseScrollFadeOptions {
  axis: 'x' | 'y';
}

/**
 * Прокручиваемая строка или колонка без видимого скроллбара: край со скрытым содержимым
 * затухает (`maskClass` на контейнер, `updateFade` на `@scroll`), `reveal` докручивает
 * до элемента, не трогая прокрутку страницы.
 */
export function useScrollFade(el: Readonly<Ref<HTMLElement | null>>, { axis }: UseScrollFadeOptions) {
  const canScrollBack = shallowRef(false);
  const canScrollForward = shallowRef(false);

  function updateFade(): void {
    const node = el.value;
    if (!node) return;

    const offset = axis === 'x' ? node.scrollLeft : node.scrollTop;
    const visible = axis === 'x' ? node.clientWidth : node.clientHeight;
    const total = axis === 'x' ? node.scrollWidth : node.scrollHeight;
    canScrollBack.value = offset > 1;
    canScrollForward.value = offset + visible < total - 1;
  }

  function reveal(target: HTMLElement | null | undefined, behavior: ScrollBehavior = 'instant'): void {
    const node = el.value;

    if (node && target) {
      const box = node.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      const before = (axis === 'x' ? rect.left - box.left : rect.top - box.top) - FADE_PX;
      const after = (axis === 'x' ? rect.right - box.right : rect.bottom - box.bottom) + FADE_PX;
      const delta = before < 0 ? before : Math.max(after, 0);

      if (delta !== 0) node.scrollBy({ [axis === 'x' ? 'left' : 'top']: delta, behavior });
    }

    updateFade();
  }

  const maskClass = computed(() => [canScrollBack.value && MASKS[axis][0], canScrollForward.value && MASKS[axis][1]]);

  useResizeObserver(el, updateFade);

  return { canScrollBack, canScrollForward, maskClass, updateFade, reveal };
}
